/**
 * Standardized TanStack Query State Management
 *
 * Provides consistent patterns for handling query states (loading, error, success)
 * with standardized error handling and loading states.
 */

import useCustomToast from "@/hooks/useCustomToast"
import {
  type AppErrorClass,
  createAppError,
  isAuthError,
  isRetryableError,
} from "@/utils/errorHandler"
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query"
import React, { useCallback, useMemo } from "react"

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface QueryState<TData> {
  data: TData | undefined
  error: AppErrorClass | null
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
  isEmpty: boolean
  isRetryable: boolean
  isAuthError: boolean
  retry: () => void
}

export interface MutationState<TData, TVariables> {
  mutate: (variables: TVariables) => void
  mutateAsync: (variables: TVariables) => Promise<TData>
  data: TData | undefined
  error: AppErrorClass | null
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
  isIdle: boolean
  reset: () => void
}

export interface QueryStateOptions {
  /** Whether to show toast notifications for errors */
  showErrorToast?: boolean
  /** Whether to show toast notifications for success */
  showSuccessToast?: boolean
  /** Custom error message for toasts */
  errorMessage?: string
  /** Custom success message for toasts */
  successMessage?: string
  /** Whether to consider empty arrays/objects as empty state */
  checkEmpty?: boolean
  /** Custom empty state checker */
  isEmpty?: (data: any) => boolean
}

// =============================================================================
// QUERY STATE HOOK
// =============================================================================

/**
 * Enhanced hook for managing TanStack Query state with standardized error handling
 */
export function useQueryState<TData = unknown>(
  queryResult: UseQueryResult<TData, unknown>,
  options: QueryStateOptions = {},
): QueryState<TData> {
  const {
    showSuccessToast: showSuccessToastMessage,
    showErrorToast: showErrorToastMessage,
  } = useCustomToast()

  const {
    showErrorToast = false,
    showSuccessToast = false,
    errorMessage,
    successMessage,
    checkEmpty = true,
    isEmpty: customIsEmpty,
  } = options

  // Convert error to AppError for consistency
  const error = useMemo(() => {
    if (!queryResult.error) return null
    return createAppError(queryResult.error, "Failed to load data")
  }, [queryResult.error])

  // Check if data is empty
  const isEmpty = useMemo(() => {
    if (!checkEmpty || !queryResult.data) return false

    if (customIsEmpty) {
      return customIsEmpty(queryResult.data)
    }

    // Default empty check
    if (Array.isArray(queryResult.data)) {
      return queryResult.data.length === 0
    }

    if (typeof queryResult.data === "object" && queryResult.data !== null) {
      return Object.keys(queryResult.data).length === 0
    }

    return false
  }, [queryResult.data, checkEmpty, customIsEmpty])

  // Error classification
  const isRetryable = useMemo(() => {
    return error ? isRetryableError(error) : false
  }, [error])

  const isAuthErrorResult = useMemo(() => {
    return error ? isAuthError(error) : false
  }, [error])

  // Toast notifications
  const showError = useCallback(() => {
    if (showErrorToast && error) {
      showErrorToastMessage(errorMessage || error.message)
    }
  }, [showErrorToast, error, errorMessage, showErrorToastMessage])

  const showSuccess = useCallback(() => {
    if (showSuccessToast && queryResult.isSuccess) {
      showSuccessToastMessage(successMessage || "Data loaded successfully")
    }
  }, [
    showSuccessToast,
    queryResult.isSuccess,
    successMessage,
    showSuccessToastMessage,
  ])

  // Show toasts when appropriate
  React.useEffect(() => {
    if (queryResult.isError && !queryResult.isFetching) {
      showError()
    }
  }, [queryResult.isError, queryResult.isFetching, showError])

  React.useEffect(() => {
    if (queryResult.isSuccess && !queryResult.isFetching) {
      showSuccess()
    }
  }, [queryResult.isSuccess, queryResult.isFetching, showSuccess])

  return {
    data: queryResult.data,
    error,
    isLoading: queryResult.isLoading || queryResult.isFetching,
    isSuccess: queryResult.isSuccess,
    isError: queryResult.isError,
    isEmpty,
    isRetryable,
    isAuthError: isAuthErrorResult,
    retry: queryResult.refetch,
  }
}

// =============================================================================
// MUTATION STATE HOOK
// =============================================================================

/**
 * Enhanced hook for managing TanStack Mutation state with standardized error handling
 */
export function useMutationState<TData = unknown, TVariables = void>(
  mutationResult: UseMutationResult<TData, unknown, TVariables>,
  options: QueryStateOptions = {},
): MutationState<TData, TVariables> {
  const {
    showSuccessToast: showSuccessToastMessage,
    showErrorToast: showErrorToastMessage,
  } = useCustomToast()

  const {
    showErrorToast = true, // Default to true for mutations
    showSuccessToast = false,
    errorMessage,
    successMessage,
  } = options

  // Convert error to AppError for consistency
  const error = useMemo(() => {
    if (!mutationResult.error) return null
    return createAppError(mutationResult.error, "Operation failed")
  }, [mutationResult.error])

  // Toast notifications
  const showError = useCallback(() => {
    if (showErrorToast && error) {
      showErrorToastMessage(errorMessage || error.message)
    }
  }, [showErrorToast, error, errorMessage, showErrorToastMessage])

  const showSuccess = useCallback(() => {
    if (showSuccessToast && mutationResult.isSuccess) {
      showSuccessToastMessage(
        successMessage || "Operation completed successfully",
      )
    }
  }, [
    showSuccessToast,
    mutationResult.isSuccess,
    successMessage,
    showSuccessToastMessage,
  ])

  // Show toasts when appropriate
  React.useEffect(() => {
    if (mutationResult.isError) {
      showError()
    }
  }, [mutationResult.isError, showError])

  React.useEffect(() => {
    if (mutationResult.isSuccess) {
      showSuccess()
    }
  }, [mutationResult.isSuccess, showSuccess])

  return {
    mutate: mutationResult.mutate,
    mutateAsync: mutationResult.mutateAsync,
    data: mutationResult.data,
    error,
    isLoading: mutationResult.isPending,
    isSuccess: mutationResult.isSuccess,
    isError: mutationResult.isError,
    isIdle: mutationResult.isIdle,
    reset: mutationResult.reset,
  }
}

// =============================================================================
// COMBINED QUERY & ASYNC BOUNDARY HOOK
// =============================================================================

/**
 * Hook that combines query state with async boundary error throwing
 */
export function useQueryWithBoundary<TData = unknown>(
  queryResult: UseQueryResult<TData, unknown>,
  options: QueryStateOptions & {
    throwOnError?: boolean
  } = {},
): QueryState<TData> {
  const { throwOnError = false, ...queryOptions } = options
  const queryState = useQueryState(queryResult, queryOptions)

  // Throw error to nearest error boundary if configured
  React.useEffect(() => {
    if (throwOnError && queryState.isError && queryState.error) {
      throw queryState.error
    }
  }, [throwOnError, queryState.isError, queryState.error])

  return queryState
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for handling multiple queries as a group
 */
export function useMultiQueryState<
  T extends Record<string, UseQueryResult<any, any>>,
>(
  queries: T,
  options: QueryStateOptions = {},
): {
  queries: { [K in keyof T]: QueryState<T[K]["data"]> }
  combined: {
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
    hasErrors: boolean
    errors: AppErrorClass[]
  }
} {
  const queryStates = useMemo(() => {
    const states = {} as { [K in keyof T]: QueryState<T[K]["data"]> }

    for (const [key, queryResult] of Object.entries(queries)) {
      states[key as keyof T] = useQueryState(queryResult, options)
    }

    return states
  }, [queries, options])

  const combined = useMemo(() => {
    const states = Object.values(queryStates)

    return {
      isLoading: states.some((state) => state.isLoading),
      isError: states.some((state) => state.isError),
      isSuccess: states.every((state) => state.isSuccess),
      hasErrors: states.some((state) => state.error !== null),
      errors: states
        .filter((state) => state.error)
        .map((state) => state.error!),
    }
  }, [queryStates])

  return {
    queries: queryStates,
    combined,
  }
}

/**
 * Hook for automatic retry with exponential backoff
 */
export function useRetryableQuery<TData = unknown>(
  queryResult: UseQueryResult<TData, unknown>,
  options: {
    maxRetries?: number
    baseDelay?: number
    maxDelay?: number
  } = {},
) {
  const { maxRetries = 3, baseDelay = 1000, maxDelay = 30000 } = options
  const [retryCount, setRetryCount] = React.useState(0)

  const queryState = useQueryState(queryResult)

  const retryWithDelay = useCallback(async () => {
    if (retryCount >= maxRetries || !queryState.isRetryable) {
      return
    }

    const delay = Math.min(baseDelay * 2 ** retryCount, maxDelay)

    setTimeout(() => {
      setRetryCount((prev) => prev + 1)
      queryState.retry()
    }, delay)
  }, [retryCount, maxRetries, baseDelay, maxDelay, queryState])

  // Auto-retry on retryable errors
  React.useEffect(() => {
    if (
      queryState.isError &&
      queryState.isRetryable &&
      retryCount < maxRetries
    ) {
      retryWithDelay()
    }
  }, [
    queryState.isError,
    queryState.isRetryable,
    retryWithDelay,
    retryCount,
    maxRetries,
  ])

  // Reset retry count on success
  React.useEffect(() => {
    if (queryState.isSuccess) {
      setRetryCount(0)
    }
  }, [queryState.isSuccess])

  return {
    ...queryState,
    retryCount,
    maxRetries,
    canRetry: retryCount < maxRetries && queryState.isRetryable,
    retryWithDelay,
  }
}

export default useQueryState
